import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';

interface ProcessPayoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorName: string;
  originalAmount: number;
  vendorVenmoHandle?: string;
  vendorPaypalEmail?: string;
  onProcess: (data: {
    adjusted_amount: number;
    adjustment_reason?: string;
    payout_method: string;
    payout_reference?: string;
    notes?: string;
  }) => Promise<void>;
}

export default function ProcessPayoutDialog({
  open,
  onOpenChange,
  vendorName,
  originalAmount,
  vendorVenmoHandle,
  vendorPaypalEmail,
  onProcess,
}: ProcessPayoutDialogProps) {
  const [adjustedAmount, setAdjustedAmount] = useState(originalAmount.toString());
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('venmo');
  const [payoutReference, setPayoutReference] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Auto-fill venmo handle when dialog opens or method changes
  useEffect(() => {
    if (open) {
      // Format amount to 2 decimal places to avoid floating point issues
      setAdjustedAmount(originalAmount.toFixed(2));
      if (payoutMethod === 'venmo' && vendorVenmoHandle) {
        setPayoutReference(vendorVenmoHandle);
      } else if (payoutMethod === 'paypal' && vendorPaypalEmail) {
        setPayoutReference(vendorPaypalEmail);
      }
    }
  }, [open, payoutMethod, vendorVenmoHandle, vendorPaypalEmail, originalAmount]);

  const handleSubmit = async () => {
    const amount = parseFloat(adjustedAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount !== originalAmount && !adjustmentReason.trim()) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }

    // Venmo handle is now optional - payment can proceed without it

    setProcessing(true);
    try {
      await onProcess({
        adjusted_amount: amount,
        adjustment_reason: adjustmentReason.trim() || undefined,
        payout_method: payoutMethod,
        payout_reference: payoutReference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      
      // Reset form
      setAdjustedAmount(originalAmount.toString());
      setAdjustmentReason('');
      setPayoutReference('');
      setNotes('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error processing payout:', error);
    } finally {
      setProcessing(false);
    }
  };

  const openVenmoPayment = () => {
    const amount = parseFloat(adjustedAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const venmoHandle = payoutReference.trim().replace('@', '');
    if (!venmoHandle) {
      toast.error('Please enter a Venmo handle');
      return;
    }

    const note = encodeURIComponent(`RecruitLook payout - ${vendorName}`);
    
    // Try Venmo app deeplink first, with web fallback
    const venmoWebUrl = `https://venmo.com/?txn=pay&audience=private&recipients=${venmoHandle}&amount=${amount.toFixed(2)}&note=${note}`;
    
    window.open(venmoWebUrl, '_blank');
    toast.success('Venmo opened in new tab. Complete the payment there.');
  };

  const openPayPalPayment = () => {
    const amount = parseFloat(adjustedAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const paypalEmail = payoutReference.trim();
    if (!paypalEmail) {
      toast.error('Please enter a PayPal email');
      return;
    }

    // PayPal.me or PayPal send money
    const paypalUrl = `https://www.paypal.com/paypalme/${paypalEmail}/${amount.toFixed(2)}`;
    
    window.open(paypalUrl, '_blank');
    toast.info('PayPal opened in new tab. You may need to enter the email manually.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Process Payout</DialogTitle>
          <DialogDescription>
            Mark payment as completed for {vendorName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Original Amount</Label>
            <div className="text-2xl font-bold text-primary">
              ${originalAmount.toFixed(2)}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adjusted-amount">Actual Amount Paid *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="adjusted-amount"
                type="text"
                inputMode="decimal"
                value={adjustedAmount}
                onChange={(e) => {
                  // Only allow numbers and one decimal point
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  // Prevent multiple decimal points
                  const parts = value.split('.');
                  if (parts.length > 2) return;
                  // Limit to 2 decimal places
                  if (parts[1] && parts[1].length > 2) return;
                  setAdjustedAmount(value);
                }}
                onBlur={() => {
                  // Format to 2 decimal places on blur
                  const num = parseFloat(adjustedAmount);
                  if (!isNaN(num)) {
                    setAdjustedAmount(num.toFixed(2));
                  }
                }}
                placeholder="0.00"
                className="pl-7"
              />
            </div>
          </div>

          {parseFloat(adjustedAmount) !== originalAmount && (
            <div className="space-y-2">
              <Label htmlFor="adjustment-reason">Adjustment Reason *</Label>
              <Textarea
                id="adjustment-reason"
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="Explain why the amount differs from the original..."
                rows={2}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="payout-method">Payment Method *</Label>
            <Select value={payoutMethod} onValueChange={setPayoutMethod}>
              <SelectTrigger id="payout-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="venmo">Venmo</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payout-reference">
              {payoutMethod === 'venmo' && 'Venmo Handle (optional)'}
              {payoutMethod === 'paypal' && 'PayPal Email (optional)'}
              {payoutMethod === 'check' && 'Check Number (optional)'}
              {payoutMethod === 'bank_transfer' && 'Transfer Reference (optional)'}
              {payoutMethod === 'other' && 'Reference (optional)'}
            </Label>
            <div className="flex gap-2">
              <Input
                id="payout-reference"
                value={payoutReference}
                onChange={(e) => setPayoutReference(e.target.value)}
                placeholder={
                  payoutMethod === 'venmo' ? '@username' :
                  payoutMethod === 'paypal' ? 'email@example.com' :
                  payoutMethod === 'check' ? 'Check #1234' :
                  'Reference number or info'
                }
                className="flex-1"
              />
              {payoutMethod === 'venmo' && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={openVenmoPayment}
                  className="whitespace-nowrap"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Pay via Venmo
                </Button>
              )}
              {payoutMethod === 'paypal' && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={openPayPalPayment}
                  className="whitespace-nowrap"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Open PayPal
                </Button>
              )}
            </div>
            {payoutMethod === 'venmo' && vendorVenmoHandle && (
              <p className="text-xs text-muted-foreground">
                Vendor's saved Venmo: {vendorVenmoHandle}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes about this payment..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={processing}>
            {processing ? 'Processing...' : 'Mark as Paid'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
